import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    FiberNewOutlined,
    HourglassTopOutlined,
    ManageAccountsOutlined,
    PersonSearchOutlined,
    MonitorHeartOutlined,
    CampaignOutlined,
    CheckCircleOutlined,
    ErrorOutlined,
    ArrowForwardOutlined,
    BusinessOutlined,
} from "@mui/icons-material";
import {
    useGetPlatformHealthQuery,
    useGetOrganizationHealthQuery,
    useGetUserHealthQuery,
} from "state/rtkQuery/health/healthQueries";
import { useGetGitHubStatusQuery } from "state/rtkQuery/githubStatus/githubStatusQueries";

import "./EcrOpsHome.css";

const TOOLS = [
    {
        id: "unified-search",
        label: "Global User Search",
        description:
            "Search for any user across all sources — Arbiter database, GitHub Enterprise, and Active Directory — from a single unified view.",
        icon: <PersonSearchOutlined />,
        to: "/ecr-ops/search",
        tag: "Search",
    },
    {
        id: "org-management",
        label: "Organisation Management",
        description:
            "View, search, filter and inline-edit all organisations in the ECR platform. Export data and manage org details directly.",
        icon: <ManageAccountsOutlined />,
        to: "/ecr-ops/org-management",
        tag: "Grid",
    },
    {
        id: "new-orgs",
        label: "New Organisations",
        description:
            "View organisations currently in In-Progress status — provisioning has been triggered but is not yet complete.",
        icon: <FiberNewOutlined />,
        to: "/ecr-ops/new-orgs",
        tag: "In-Progress",
    },
    {
        id: "health",
        label: "System Health",
        description:
            "Real-time health monitoring for all ECR platform services. Auto-refreshes every 30 seconds to track service status.",
        icon: <MonitorHeartOutlined />,
        to: "/ecr-ops/health",
        tag: "Monitor",
    },
    {
        id: "copilot-enterprise",
        label: "Enterprise Usage",
        description:
            "GitHub Copilot AI Credit usage and spend, aggregated across every organization — no org scoping.",
        icon: <BusinessOutlined />,
        to: "/ecr-ops/copilot-enterprise",
        tag: "Copilot",
    },
    {
        id: "stuck-users",
        label: "Stuck Users",
        description:
            "Identify users whose membership requests have been in Requested or Remove-Requested state for over 24 hours across all orgs.",
        icon: <HourglassTopOutlined />,
        to: "/ecr-ops/stuck-users",
        tag: "Monitor",
    },
    {
        id: "portal-config",
        label: "Portal Configuration",
        description:
            "Manage Home Dashboard announcement banners and Copilot dashboard settings — edit, reorder, enable or disable without a frontend deploy.",
        icon: <CampaignOutlined />,
        to: "/ecr-ops/portal-config",
        tag: "Config",
    },
];

const ToolCard = ({ tool }) => {
    const navigate = useNavigate();

    return (
        <button
            className="ecr-home-card"
            onClick={() => navigate(tool.to)}
            type="button"
        >
            <div className="ecr-home-card__icon-wrap">{tool.icon}</div>
            <div className="ecr-home-card__body">
                <div className="ecr-home-card__header">
                    <span className="ecr-home-card__label">{tool.label}</span>
                    <span className="ecr-home-card__tag">{tool.tag}</span>
                </div>
                <p className="ecr-home-card__desc">{tool.description}</p>
            </div>
            <div className="ecr-home-card__arrow">›</div>
        </button>
    );
};

ToolCard.propTypes = {
    tool: PropTypes.shape({
        to: PropTypes.string.isRequired,
        icon: PropTypes.node.isRequired,
        label: PropTypes.string.isRequired,
        tag: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
    }).isRequired,
};

// ── Status indicator ──────────────────────────────────────────────────────────

const StatusIndicator = ({ label, status = null, isLoading = false }) => {
    const isHealthy = status?.toLowerCase() === "healthy";

    let badge;
    if (isLoading) {
        badge = (
            <span className="ecr-home-status-item__badge ecr-home-status-item__badge--loading">
                <span className="ecr-home-status-dot ecr-home-status-dot--loading" />{" "}
                Checking...
            </span>
        );
    } else if (isHealthy) {
        badge = (
            <span className="ecr-home-status-item__badge ecr-home-status-item__badge--healthy">
                <CheckCircleOutlined fontSize="inherit" />
                Healthy
            </span>
        );
    } else {
        badge = (
            <span className="ecr-home-status-item__badge ecr-home-status-item__badge--error">
                <ErrorOutlined fontSize="inherit" />
                {status || "Unknown"}
            </span>
        );
    }

    return (
        <div className="ecr-home-status-item">
            <span className="ecr-home-status-item__label">{label}</span>
            {badge}
        </div>
    );
};

StatusIndicator.propTypes = {
    label: PropTypes.string.isRequired,
    status: PropTypes.string,
    isLoading: PropTypes.bool,
};


const EcrOpsHome = () => {
    const authState = useSelector((state) => state.auth);
    const navigate = useNavigate();
    const givenName = authState?.userAccount?.givenName || "";
    const surname = authState?.userAccount?.surname || "";
    const fullName = givenName && surname ? `${givenName} ${surname}` : givenName || "Operations Team";

    // Query health status for all services
    const { data: userHealth, isLoading: userLoading } = useGetUserHealthQuery(undefined, {
        pollingInterval: 30000,
    });
    const { data: orgHealth, isLoading: orgLoading } = useGetOrganizationHealthQuery(undefined, {
        pollingInterval: 30000,
    });
    const { data: platformHealth, isLoading: platformLoading } = useGetPlatformHealthQuery(undefined, {
        pollingInterval: 30000,
    });
    const { data: githubStatus, isLoading: githubStatusLoading } = useGetGitHubStatusQuery(undefined, {
        pollingInterval: 60000,
    });

    return (
        <div>
            {/* Page header */}
            <div className="ecr-ops-page-header">
                <p className="ecr-ops-page-header__eyebrow">Operations</p>
                <h1 className="ecr-ops-page-header__title">ECR Ops Portal</h1>
                <p className="ecr-ops-page-header__sub">
                    Internal tooling for the ECR operations team — manage organisations, look up users, and monitor platform health.
                </p>
            </div>

            <div className="ecr-ops-content">
                {/* Welcome banner */}
                <div className="ecr-home-banner">
                    <div className="ecr-home-banner__content">
                        <p className="ecr-home-banner__title">Welcome, {fullName}</p>
                        <p className="ecr-home-banner__body">
                            Select a tool below to get started. All actions are performed via the ECR
                            platform APIs and are logged for audit purposes.
                        </p>
                    </div>
                </div>

                {/* System status bar */}
                <div className="ecr-home-status-bar">
                    <div className="ecr-home-status-bar__header">
                        <MonitorHeartOutlined />
                        <span>System Status</span>
                    </div>
                    <div className="ecr-home-status-bar__services">
                        <StatusIndicator
                            label="User Service"
                            status={userHealth?.status}
                            isLoading={userLoading}
                        />
                        <StatusIndicator
                            label="Organization Service"
                            status={orgHealth?.status}
                            isLoading={orgLoading}
                        />
                        <StatusIndicator
                            label="Platform Service"
                            status={platformHealth?.status}
                            isLoading={platformLoading}
                        />
                        <StatusIndicator
                            label="GitHub Status"
                            status={githubStatus?.status === "healthy" ? "healthy" : githubStatus?.description}
                            isLoading={githubStatusLoading}
                        />
                    </div>
                    <button
                        className="ecr-home-status-bar__details-btn"
                        onClick={() => navigate("/ecr-ops/health")}
                        type="button"
                    >
                        <span>View Details</span>
                        <ArrowForwardOutlined fontSize="small" />
                    </button>
                </div>

                {/* Tool grid */}
                <div className="ecr-home-grid">
                    {TOOLS.map((tool) => (
                        <ToolCard key={tool.id} tool={tool} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default EcrOpsHome;
